import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { SankeyChartComponent, SbSankeyModel } from '@ngx-seatback/charts';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  ScreenFullDTO,
  VisitorTrackerDTO,
  VisitorTrackerScreenAssignmentDTO,
  VisitorTrackerScreenDTO,
} from '../../../../../services/api/api';
import { SbModalState } from '../../../../../services/gui/modal/modal-state.service';

@Component({
  selector: 'sb-comp-location-tracker-assignment-to-tracker',
  templateUrl: './location-tracker-assignment-to-tracker.component.html',
  styleUrls: ['./location-tracker-assignment-to-tracker.component.scss'],
  standalone: false,
})
export class LocationTrackerAssignmentToTrackerComponent implements OnInit {
  @ViewChild('sankey', { static: false }) sankey: SankeyChartComponent;

  @Input() isInModalContext: any;

  selectedData: { screens: ScreenFullDTO; tracker: VisitorTrackerDTO[]; assignments: VisitorTrackerScreenAssignmentDTO[] };
  screenForm: UntypedFormGroup;
  sankeyData: SbSankeyModel[] = [];
  loading: boolean;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private modalState: SbModalState
  ) {
    this.loading = true;
    this.screenForm = this.formBuilder.group({
      screens: this.formBuilder.array([]),
    });
  }

  screens(): UntypedFormArray {
    return this.screenForm.get('screens') as UntypedFormArray;
  }

  ngOnInit(): void {
    if (this.isInModalContext) {
      this.selectedData = this.modalState.options.get(this.isInModalContext).payload;
      if (this.selectedData) {
        this.loadAssignments();
      }
    }
  }

  loadAssignments() {
    for (const ass of this.selectedData.assignments) {
      this.addAssignment(ass);
    }
    this.convertToChartModel(this.selectedData).then();
  }

  addAssignmentByButton() {
    this.addAssignment();
  }

  addAssignment(ass?: any) {
    const assignment = this.formBuilder.group({
      tracker: new UntypedFormControl(ass ? this.findSelectedTracker(ass.tracker) : ''),
      screen: new UntypedFormControl(ass ? ass.screen : ''),
      value: new UntypedFormControl(ass ? ass.amount : ''),
    });
    this.screens().push(assignment);
  }

  deleteRow(i: number) {
    this.loading = true;
    const delTrackerId = this.screenForm.get(['screens', i, 'tracker']).value.id;

    this.removeAssignment(delTrackerId).then(() => {
      this.screens().removeAt(i);
      this.sankey.updateData(this.sankeyData);
    });
  }

  removeAssignment(delTrackerId: number) {
    return new Promise(resolve => {
      if (delTrackerId) {
        for (const assign of this.sankeyData) {
          if (assign.from.toString() === this.attachPrefix(delTrackerId)) {
            assign.value = undefined;
            break;
          }
        }
      }
      resolve(true);
    });
  }

  convertToChartModel(data) {
    return new Promise(resolve => {
      this.sankeyData = [];
      this.loading = true;
      for (const tracker of data.tracker) {
        this.sankeyData.push({
          from: this.attachPrefix(tracker.id),
          to: this.attachPrefix(this.selectedData.screens.id),
          value: undefined,
          additionalData: {
            from: tracker,
            to: this.selectedData.screens,
          },
        });
      }
      for (const ass of data.assignments) {
        for (const singleData of this.sankeyData) {
          if (singleData.from.toString() === this.attachPrefix(ass.tracker.toString())) {
            if (singleData.to === this.attachPrefix(this.selectedData.screens.id)) {
              singleData.value = ass.amount;
            }
          }
        }
      }
      this.loading = false;
      resolve(true);
    });
  }

  save() {
    const savedData: VisitorTrackerScreenDTO[] = [];
    const assignments = this.screens().controls;
    for (let i = 0; i < assignments.length; i++) {
      const assignment: VisitorTrackerScreenDTO = {
        visitorTracker: this.screenForm.get(['screens', i, 'tracker']).value,
        screen: this.selectedData.screens,
        amount: Number(this.screenForm.get(['screens', i, 'value']).value),
      };
      savedData.push(assignment);
    }
    this.modalState.modals.get(this.isInModalContext).close(savedData);
  }

  cancel() {
    this.modalState.modals.get(this.isInModalContext).close(null);
  }

  updateValue(i: number) {
    const from = this.screenForm.get(['screens', i, 'tracker']).value.id;
    let index: number;
    for (const [idx, singleSankey] of this.sankeyData.entries()) {
      if (singleSankey.from === this.attachPrefix(from)) {
        index = idx;
      }
    }
    if (i >= 0 && i <= this.sankeyData.length - 1) {
      this.sankeyData[index].from = this.attachPrefix(this.screenForm.get(['screens', i, 'tracker']).value.id);
      this.sankeyData[index].to = this.attachPrefix(this.selectedData.screens.id);
      this.sankeyData[index].additionalData.to = this.selectedData.screens;
      this.sankeyData[index].additionalData.from = this.screenForm.get(['screens', i, 'tracker']).value;
      this.sankeyData[index].value = Number(this.screenForm.get(['screens', i, 'value']).value);
      this.sankey.updateData(this.sankeyData);
    } else {
      this.sankeyData.push({
        from: this.attachPrefix(this.screenForm.get(['screens', i, 'tracker']).value.id),
        to: this.attachPrefix(this.selectedData.screens.id),
        value: Number(this.screenForm.get(['screens', i, 'value']).value),
        additionalData: {
          from: this.screenForm.get(['screens', i, 'tracker']).value,
          to: this.selectedData.screens,
        },
      });
    }
  }

  findSelectedTracker(id: number): VisitorTrackerDTO | null {
    if (this.selectedData.tracker) {
      for (const d of this.selectedData.tracker) {
        if (d.id === Number(id)) {
          return d;
        }
      }
      return null;
    }
  }

  isDisplayed(tracker: VisitorTrackerDTO) {
    for (const ass of this.screens().controls) {
      if (ass?.value?.tracker?.id?.toString() === tracker?.id?.toString()) {
        return true;
      }
    }
    return false;
  }

  attachPrefix(element: number): string {
    return 'ID: ' + element.toString();
  }
}
